import { Paper, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import {
  GRAPH_NODE_PAPER_PROPS,
  NODE_TEXT_MAX_WIDTH
} from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import { GraphNodeGlyphIcon } from 'components/scenarios/concurrent/GraphNodeGlyphIcon'
import {
  isExternalActionNode,
  isRollbackExternalActionNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import { muiTheme } from 'mui-theme'
import {
  type ExternalActionNode,
  type ExternalActionRequest,
  ExternalActionRequestType,
  type GraphNode,
  type RollbackExternalActionNode,
  ScenarioLifecycle
} from 'types/api/scenarios/scenario'
import {
  getAWSAutoscalingGroupNameFromASGArn,
  getAWSLoadBalancerNameFromLoadBalancerArn
} from 'utils/status-checks/status-check-integrations-util-aws'

function getExternalActionText(externalActionRequest: ExternalActionRequest) {
  switch (externalActionRequest.type) {
    case ExternalActionRequestType.DisableAWSAutoScalingGroups:
      return 'Disable'
    case ExternalActionRequestType.RemoveAWSSecurityGroups:
      return 'Remove'
    default:
      return 'Re-enable'
  }
}

function getExternalActionTargetPrefix(
  externalActionRequest: ExternalActionRequest
) {
  switch (externalActionRequest.type) {
    case ExternalActionRequestType.DisableAWSAutoScalingGroups:
      return 'ASG autoscaling on'
    default:
      return 'Security Group from'
  }
}

export const ExternalActionGraphNode: React.FC<{
  node: ExternalActionNode | RollbackExternalActionNode
  isClickable?: boolean
  rowActions?: React.ReactNode
  onClick?: (node: GraphNode) => void
}> = ({ node, isClickable = false, rowActions, onClick }) => {
  const { externalActionRequest } = node

  const isDisablingAction =
    isExternalActionNode(node) &&
    (externalActionRequest.type ===
      ExternalActionRequestType.DisableAWSAutoScalingGroups ||
      externalActionRequest.type ===
        ExternalActionRequestType.RemoveAWSSecurityGroups)

  const actionColor = isDisablingAction ? 'error' : 'success.main'

  const message = useMemo(
    () => ({
      action: isRollbackExternalActionNode(node)
        ? 'Re-enable'
        : getExternalActionText(externalActionRequest),
      target: getExternalActionTargetPrefix(externalActionRequest)
    }),
    [externalActionRequest, node]
  )

  const resourceName =
    externalActionRequest.type ===
    ExternalActionRequestType.RemoveAWSSecurityGroups
      ? getAWSLoadBalancerNameFromLoadBalancerArn(
          externalActionRequest.resourceArn
        )
      : getAWSAutoscalingGroupNameFromASGArn(externalActionRequest.resourceArn)

  return (
    <Paper
      data-testid={`external-action-graph-node-${node.guid}`}
      sx={[
        GRAPH_NODE_PAPER_PROPS,
        !rowActions && { paddingRight: 2 },
        (muiTheme) => ({
          marginY: muiTheme.spacing(2)
        })
      ]}
      {...(isClickable && onClick && { onClick: () => onClick(node) })}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <GraphNodeGlyphIcon node={node} />
          <Stack sx={{ maxWidth: '100%' }}>
            <TruncatedTextWithTooltip
              sx={{
                maxWidth: NODE_TEXT_MAX_WIDTH
              }}
              tooltipProps={{
                title: `${message.action} ${message.target} ${resourceName}`
              }}
              variant="body1"
            >
              <Typography
                color={actionColor}
                component="span"
                fontWeight="bold"
                variant="inherit"
              >
                {message.action}
              </Typography>{' '}
              <Typography component="span" variant="inherit">
                {message.target}{' '}
                <Typography
                  component="span"
                  fontWeight="bold"
                  variant="inherit"
                >
                  {resourceName}
                </Typography>
              </Typography>
            </TruncatedTextWithTooltip>
            {node.state.lifecycle === ScenarioLifecycle.Failed ? (
              <Stack
                alignItems="center"
                direction="row"
                flexWrap="nowrap"
                spacing={0.5}
              >
                <Typography color="error">
                  <Icon
                    css={{ marginRight: muiTheme.spacing(0.5) }}
                    icon="solidWarning"
                  />
                  <Typography color="inherit" variant="caption">
                    {node.state.failed_reason
                      ? `This action did not occur because ${node.state.failed_reason}`
                      : 'An unknown error occurred'}
                  </Typography>
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Stack>

        {rowActions && (
          <Stack
            sx={{
              justifyContent: 'center',
              alignContent: 'center'
            }}
          >
            {rowActions}
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}
